import { Card, CardBody, CardHeader } from "@flick/fep-library";
import { ReactComponent as ForwardIcon } from "@flick/front_end_patterns/src/images/icon_nav_arrow_forward.svg";
import { useQuery } from "@tanstack/react-query";
import { BillSmootherTransactions } from "components/BillSmoother/BillSmootherTransactions";
import { ComponentItemFallback } from "components/fallback/ComponentItemFallback";
import { LoadingSection } from "components/LoadingSection";
import {
  getBillingBalance,
  getBillSmootherTransactions,
} from "queries/billing";
import { getBillSmootherConfig } from "queries/payment_smoother";
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { Link } from "react-router-dom";
import { setBillSmootherConfig } from "reducers/billSmoother";
import { useAppDispatch, useAppSelector } from "reduxUtils/hook";
import { NZD } from "utils/currency";
import { jwtClient } from "utils/jwtClient";
import { refetchOnScroll } from "utils/refetchOnScroll";

export const BillSmootherPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { customerNumber } = useAppSelector((store) => store.currentAccount);
  const { billingEntityData } = useAppSelector(
    (store) => store.billingEntityData,
  );
  const { billSmootherConfig: billSmootherConfigState } = useAppSelector(
    (store) => store.billSmootherConfig,
  );
  const [storedTransactions, setStoredTransactions] = useState([]);
  const [nextEndDate, setNextEndDate] = useState(new Date());

  const { data: billSmootherConfig, isError: isBillSmootherConfigError } =
    useQuery(
      ["billSmootherConfig"],
      () => getBillSmootherConfig(billingEntityData?.id, jwtClient),
      { enabled: !billSmootherConfigState && Boolean(billingEntityData?.id) },
    );

  const { data: billSmootherBalance } = useQuery(
    ["billSmootherBalance", { customerNumber, jwtClient }],
    getBillingBalance,
    {
      enabled:
        Boolean(billSmootherConfigState) && billSmootherConfigState.enrolled,
    },
  );

  useEffect(() => {
    if (!billSmootherConfigState && billSmootherConfig) {
      dispatch(setBillSmootherConfig({ billSmootherConfig }));
    }
  }, [billSmootherConfig]);

  const {
    data: transactions,
    isFetching: isTransactionFetching,
    isSuccess: isTransactionSuccess,
  } = useQuery(
    ["billSmootherTransactions", nextEndDate],
    () => getBillSmootherTransactions(customerNumber, nextEndDate, jwtClient),
    { enabled: Boolean(customerNumber), keepPreviousData: true },
  );

  useEffect(() => {
    let nextDate = nextEndDate;
    if (transactions?.length) {
      nextDate = new Date(
        transactions[transactions.length - 1].attributes.timestamps.created_at,
      );
      setStoredTransactions(storedTransactions.concat(transactions));
    }
    return refetchOnScroll(transactions, setNextEndDate, nextDate);
  }, [transactions]);

  if (isBillSmootherConfigError) return <Navigate to="/bills" />;

  return (
    <div className="page_section">
      <section className="page_section__column page_section__column--1">
        <Card cardBorderStyle="shadow" cardPaddingType="compact">
          <CardHeader headingColor="black">
            <h2 className="h4">Your Bill Smoother account</h2>
          </CardHeader>

          <CardBody>
            <article className="customer_tools_section">
              {billSmootherBalance &&
                !isNaN(billSmootherBalance.total_credits?.amount) && (
                  <>
                    <section>
                      <h3 className="customer_tools_section__text">
                        Account credit
                      </h3>
                      <span className="h2" data-testid="billSmootherValue">
                        {NZD.format(billSmootherBalance.total_credits.amount)}
                      </span>
                    </section>
                    <span className="spacer"></span>
                  </>
                )}
              <section className="customer_tools_section__item customer_tools_section__item--borders">
                <h3 className="h4">Settings</h3>
                <span className="spacer"></span>
                <h4 className="h6">Minimum payment amount:</h4>
                <p
                  className="customer_tools_section__text"
                  data-testid="contribution_threshold"
                >
                  <strong aria-hidden className="color_green">
                    +{" "}
                  </strong>
                  You‘re contributing at least{" "}
                  <strong className="color_green">
                    {NZD.format(
                      +billSmootherConfigState?.contribution_threshold,
                    )}
                  </strong>{" "}
                  at every bill
                </p>
                <span className="spacer"></span>
                <h4 className="h6">When to use your Bill Smoother credit:</h4>
                <p
                  className="customer_tools_section__text"
                  data-testid="withdrawal_trigger"
                >
                  <strong aria-hidden className="color_red">
                    -{" "}
                  </strong>
                  Help pay for bills over{" "}
                  <strong className="color_red">
                    {NZD.format(+billSmootherConfigState?.withdrawal_trigger)}
                  </strong>
                </p>
              </section>
              <section className="customer_tools_section__link_container">
                <Link
                  className="customer_tools_section__link"
                  to="/bills/bill-smoother/settings"
                >
                  <strong>Your Bill Smoother settings</strong>
                </Link>
                <ForwardIcon />
              </section>
              <section className="customer_tools_section__link_container">
                <Link
                  to="/bills/bill-smoother/more-information"
                  className="customer_tools_section__link"
                >
                  <strong>How does Bill Smoother work?</strong>
                </Link>
                <ForwardIcon />
              </section>
            </article>
          </CardBody>
        </Card>
      </section>
      <section className="page_section_column page_section__column--2">
        <article>
          <h2 className="page__heading">Transactions</h2>
          {isTransactionFetching && <LoadingSection />}

          {!isTransactionFetching && !isTransactionSuccess && (
            <>
              <div className="spacer" />
              <ComponentItemFallback componentTitle="Bill Smoother transactions" />
            </>
          )}

          {isTransactionSuccess && (
            <BillSmootherTransactions
              startDate={new Date()}
              transactions={storedTransactions}
            />
          )}
        </article>
      </section>
    </div>
  );
};
